import type { UserCreateSchema } from '@repo/schemas/user/userCreate';
import type { UserPrivate } from '@repo/types/users';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useLinkParent } from '@repo/api/parent/useLinkParent';
import { usePostUser } from '@repo/api/user/usePostUser';
import { cn } from '@repo/lib/cn';
import { generatePassword } from '@repo/lib/generatePassword';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { userErrorMessages } from '@repo/lib/messages/userErrorMessages';
import { userCreateSchema } from '@repo/schemas/user/userCreate';
import { AccountType } from '@repo/types/enums/accountType';
import { UserLvl } from '@repo/types/enums/userLvl';
import { CheckCircle2Icon, CheckIcon, ChevronsUpDownIcon, CopyIcon, Download, PlusIcon, UserIcon, XIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EmptyState, EmptyStateContent, EmptyStateDescription, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Form } from '@/components/ui/form';
import { LoadingOverlay, LoadingOverlayLabel, LoadingOverlaySpinner } from '@/components/ui/loading-overlay';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useCopyToClipboard } from '@/hooks/copy-to-clipboard';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { getAvatar } from '@/utils/getAvatar';

import type { NewParentDraft } from './CreateUserParentSection';

import { CreatedParentAccounts } from './CreatedParentAccounts';
import { CreateUserParentSection } from './CreateUserParentSection';
import { UserFormFields } from './UserFormFields';

/**
 * Props for the CreateUserDialog component.
 * @property open - Whether the dialog is open.
 * @property onOpenChange - Callback to change dialog open state.
 * @property accountType - Account type to create. Defaults to standard user; pass `AccountType.Parent` to create a parent and (optionally) link children inline.
 * @property students - When creating a parent, the list of students available for linking.
 * @property parents - When creating a student, the list of parents available for linking.
 */
interface CreateUserDialogProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  accountType?: AccountType;
  students?: UserPrivate[];
  parents?: UserPrivate[];
}

export default function CreateUserDialog(createUserProps: CreateUserDialogProps) {
  const { open, onOpenChange, accountType = AccountType.User, students = [], parents = [] } = createUserProps;
  const isParent = accountType === AccountType.Parent;

  const [password, setPassword] = useState<string>(generatePassword(10));
  const [page, setPage] = useState<number>(0);
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);
  const [studentPickerOpen, setStudentPickerOpen] = useState(false);
  const [selectedParentIds, setSelectedParentIds] = useState<number[]>([]);
  const [newParents, setNewParents] = useState<NewParentDraft[]>([]);
  const [createdParents, setCreatedParents] = useState<NewParentDraft[]>([]);

  const selectedStudents = useMemo(() => students.filter((s) => selectedStudentIds.includes(s.id)), [students, selectedStudentIds]);
  const availableStudents = useMemo(() => students.filter((s) => !selectedStudentIds.includes(s.id)), [students, selectedStudentIds]);

  const { mutateAsync: linkParent } = useLinkParent();

  const [copied, setCopied] = useState<boolean>(false);
  const [, copy] = useCopyToClipboard();

  const handleCopy = async () => {
    if (!password) return;
    try {
      await copy(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const { mutateAsync, isPending } = usePostUser();

  const onClose = () => {
    onOpenChange(false);
    form.reset();
    setPassword(generatePassword(10));
    setPage(0);
    setSelectedStudentIds([]);
    setSelectedParentIds([]);
    setNewParents([]);
    setCreatedParents([]);
  };

  const form = useForm<UserCreateSchema>({
    values: {
      name: '',
      surname: '',
      email: '',
      phone: null,
      birthdate: null,
      lvl: isParent ? UserLvl.STUDENT : 0,
      password: password,
      accountType: isParent ? AccountType.Parent : AccountType.User,
    },
    mode: 'onBlur',
    resolver: standardSchemaResolver(userCreateSchema),
  });

  const lvl = useWatch({ control: form.control, name: 'lvl' });
  const showParentSection = !isParent && lvl === UserLvl.STUDENT;

  const onSubmit = async (data: UserCreateSchema) => {
    try {
      const created = await mutateAsync({
        ...data,
        ...(isParent && {
          accountType: AccountType.Parent,
          lvl: UserLvl.STUDENT,
        }),
      });

      const linkTasks = isParent
        ? selectedStudentIds.map((studentId) => linkParent({ parentId: created.id, studentId }))
        : selectedParentIds.map((parentId) => linkParent({ parentId, studentId: created.id }));
      const newParentTasks = isParent
        ? []
        : newParents.map(async (draft) => {
            const { password: parentPassword, ...parentData } = draft;
            const parent = await mutateAsync({
              ...parentData,
              lvl: UserLvl.STUDENT,
              password: parentPassword,
              accountType: AccountType.Parent,
            });
            await linkParent({ parentId: parent.id, studentId: created.id });
            return draft;
          });

      const [linkResults, newParentResults] = await Promise.all([Promise.allSettled(linkTasks), Promise.allSettled(newParentTasks)]);
      const [firstFailedLink] = linkResults.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (firstFailedLink) {
        toasterMessageHandler(baseErrorMessages, firstFailedLink.reason);
      }
      // Drafted-parent failures are account-creation errors (e.g. duplicate
      // email), so they map through the user catalog instead of the link one.
      const [firstFailedParent] = newParentResults.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (firstFailedParent) {
        toasterMessageHandler(userErrorMessages, firstFailedParent.reason);
      }

      // allSettled preserves input order, so the fulfilled drafts stay in the
      // order they were entered.
      setCreatedParents(newParentResults.filter((r): r is PromiseFulfilledResult<NewParentDraft> => r.status === 'fulfilled').map((r) => r.value));
      setPage(1);
    } catch (error) {
      toasterMessageHandler(userErrorMessages, error instanceof Error ? error : null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'sm:h-[85dvh] sm:min-h-96 sm:max-w-108'} onCloseAutoFocus={onClose} onEscapeKeyDown={onClose}>
        <Form {...form}>
          <DialogBody>
            <LoadingOverlay isLoading={isPending}>
              <LoadingOverlaySpinner />
              <LoadingOverlayLabel>Trwa tworzenie użytkownika...</LoadingOverlayLabel>
            </LoadingOverlay>
            <DialogHeader className={'border-border-primary bg-card items-center border-b'}>
              <FeaturedIcon size={'lg'}>
                <UserIcon />
              </FeaturedIcon>
              <div className={'flex min-w-0 grow flex-col gap-0.5'}>
                <DialogTitle>{isParent ? 'Utwórz Rodzica' : 'Utwórz Użytkownika'}</DialogTitle>
                <DialogDescription className={'text-muted-foreground text-xs'}>
                  {isParent ? 'Wprowadź dane konta rodzica i opcjonalnie powiąż uczniów.' : 'Wprowadź wymagane dane użytkownika.'}
                </DialogDescription>
              </div>
            </DialogHeader>
            {page === 0 ? (
              <form className={'flex grow flex-col gap-3 overflow-auto p-4'} onSubmit={form.handleSubmit(onSubmit)} noValidate>
                <UserFormFields hideLvl={isParent} />
                {showParentSection && (
                  <CreateUserParentSection
                    parents={parents}
                    selectedParentIds={selectedParentIds}
                    onSelectedParentIdsChange={setSelectedParentIds}
                    newParents={newParents}
                    onNewParentsChange={setNewParents}
                  />
                )}
                {isParent && (
                  <div className={'space-y-3'}>
                    <h5 className={'font-medium'}>
                      Powiąż uczniów <small className={'text-text-tertiary text-sm font-normal'}>(opcjonalne)</small>
                    </h5>
                    <div className={'flex flex-col gap-2'}>
                      {selectedStudents.length === 0 ? (
                        <EmptyState className={'border-border-secondary bg-muted/25 w-full rounded-md border pt-4!'}>
                          <EmptyStateContent>
                            <EmptyStateTitle className={'text-sm!'}>Brak powiązanych uczniów</EmptyStateTitle>
                            <EmptyStateDescription className={'max-w-xs text-xs!'}>Powiąż ucznia z tym rodzicem.</EmptyStateDescription>
                          </EmptyStateContent>
                        </EmptyState>
                      ) : (
                        <ul className={'flex flex-col gap-2'}>
                          {selectedStudents.map((student) => (
                            <li key={student.id} className={'border-border-primary bg-card/75 flex items-center gap-3 rounded-md border p-2'}>
                              <Avatar size={'sm'}>
                                <AvatarImage src={student.avatarURL ? getAvatar(student.avatarURL) : undefined} />
                                <AvatarFallback>
                                  <UserIcon className={'size-3.5'} />
                                </AvatarFallback>
                              </Avatar>
                              <div className={'flex min-w-0 grow flex-col'}>
                                <p className={'truncate text-sm'}>
                                  {student.name} {student.surname}
                                </p>
                                <p className={'text-text-tertiary truncate text-xs'}>{student.email}</p>
                              </div>
                              <Button
                                size={'sm'}
                                variant={'ghost'}
                                variantColor={'destructive'}
                                onClick={() => setSelectedStudentIds((ids) => ids.filter((id) => id !== student.id))}
                                aria-label={`Usuń ${student.name} ${student.surname}`}
                              >
                                <XIcon />
                              </Button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <Popover open={studentPickerOpen} onOpenChange={setStudentPickerOpen}>
                        <PopoverTrigger asChild>
                          <Button type={'button'} variant={'outline'} role={'combobox'} className={'w-full justify-between'}>
                            <span className={'flex items-center gap-2'}>
                              <PlusIcon className={'size-4'} />
                              Powiąż ucznia
                            </span>
                            <ChevronsUpDownIcon className={'text-muted-foreground'} />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className={'min-w-xs p-0'} align={'start'} disablePortal>
                          <Command>
                            <CommandInput placeholder={'Szukaj ucznia...'} />
                            <CommandList>
                              <CommandEmpty>{availableStudents.length === 0 ? 'Brak dostępnych uczniów.' : 'Nie znaleziono ucznia.'}</CommandEmpty>
                              <CommandGroup>
                                {availableStudents.map((student) => (
                                  <CommandItem
                                    key={student.id}
                                    value={`${student.name} ${student.surname} ${student.email}`}
                                    onSelect={() => {
                                      setSelectedStudentIds((ids) => [...ids, student.id]);
                                      setStudentPickerOpen(false);
                                    }}
                                    className="h-min"
                                  >
                                    <Avatar size={'sm'}>
                                      <AvatarImage src={student.avatarURL ? getAvatar(student.avatarURL) : undefined} />
                                      <AvatarFallback>
                                        <UserIcon className={'size-3.5'} />
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className={'flex min-w-0 flex-col'}>
                                      <p className={'truncate text-sm'}>
                                        {student.name} {student.surname}
                                      </p>
                                      <p className={'text-text-tertiary truncate text-xs'}>{student.email}</p>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                )}
              </form>
            ) : (
              <div className={'flex grow flex-col gap-3 p-4'}>
                {!isParent && (
                  <div>
                    <h5 className={'pb-2 text-sm'}>Karta użytkownika</h5>
                    <div className={'border-border-primary flex gap-3 border-t py-2'}>
                      <p className={'text-muted-foreground text-xs'}>Pobierz kartę użytkownika ( Karta zostanie automatycznie wysłana użytkownikowi na wskazany adres email )</p>
                      <Button variant={'outline'} className="shrink-0" disabled>
                        <Download />
                        Pobierz
                      </Button>
                    </div>
                  </div>
                )}
                <div>
                  <h5 className={'pb-2 text-sm'}>Hasło</h5>
                  <div className={'border-border-primary flex gap-3 border-t py-2'}>
                    <p className={'text-muted-foreground w-full text-xs'}>Hasło jest generowane i przydzielane do użytkownika automatycznie.</p>
                    <Button variant={'outline'} className="shrink-0" onClick={handleCopy} aria-label={copied ? 'Copied' : 'Copy to clipboard'} disabled={copied}>
                      <div className={cn('transition-all', copied ? 'scale-100 opacity-100' : 'scale-0 opacity-0')}>
                        <CheckIcon className="text-success" aria-hidden="true" />
                      </div>
                      <div className={cn('absolute left-3 transition-all', copied ? 'scale-0 opacity-0' : 'scale-100 opacity-100')}>
                        <CopyIcon aria-hidden="true" />
                      </div>
                      Kopiuj
                    </Button>
                  </div>
                </div>
                <CreatedParentAccounts parents={createdParents} />
              </div>
            )}
          </DialogBody>
          <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
            {page === 0 ? (
              <>
                <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
                  Anuluj
                </Button>
                <Button disabled={isPending} onClick={form.handleSubmit(onSubmit)} type={'submit'} variant={'primary'} className={'w-full md:w-fit'}>
                  <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
                    <CheckCircle2Icon />
                    Zapisz
                  </LoadingSwap>
                </Button>
              </>
            ) : (
              <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
                Zamknij
              </Button>
            )}
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
